import styled from "styled-components";
import React, {
  FC,
  useContext, useMemo,
} from "react";
import { Header } from "./header/Header";
import {Box, Paper} from "@mui/material";
import {a11yStandardKeys, borderRadius, propertyCategoryKeys} from "../constants/constants";
import { A11yStandardType } from "../models/A11yStandard";
import "../translations/i18n";
import { Property } from "../models/Property";
import { BuildArea } from "../models/BuildArea";
import Tabs from "@mui/material/Tabs";
import { Tab as MuiTab }  from "@mui/material";
import { Mixpanel } from "../analytics/Mixpanel";
import { MetricMessage } from "../analytics/metrics";
import {useElementHeight} from "../hooks/useElementHeight";
import {getTextByCurrentLanguage} from "../utils/language";
import {useTranslation} from "react-i18next";
import {MAIN_TAB, TabKey, WidgetTab} from "../constants/tabs";
import Icons from "../components/Icons";
import {TabsContext} from "../contexts/tabsContext";

const AppContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const ContentWrapper = styled.div`
  height: 100%;
`

const StyledCloseIcons = styled(Icons.CloseIcon)`
  width: 12px;
  height: 12px
`

const BlockLabel = styled.div`
  display: flex; 
  align-items: center; 
  justify-content: center;
`

const EmulatedButton = styled.div`
  padding: 5px;
  border-radius: 50%;
  transition: 0.25s;
  margin-left: 5px;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`

type MainContentProps = {
  height?: number | string;
}

const MainContent = styled(Paper)<MainContentProps>`
  ${(props) => props.theme.breakpoints.down("sm")} {
    border-radius: 0px !important;
  }
  height: ${(props) => props.height};
  background-color: #f2f4f8 !important;
  background: ${(props) => props.theme.palette.background.default};
  box-shadow: none;
  border-radius: 0px 0px ${borderRadius}px 0px !important;
  
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }
  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background-color: transparent;
  box-shadow: 0 12px 24px rgba(37, 42, 49, 0.24),
    0 8px 60px rgba(37, 42, 49, 0.32);
  @media (max-width: 768px) {
    height: 100vh;
    overflow-y: scroll !important;
  }
`;

const Tab = styled(MuiTab)`
  min-height: 25px;
  min-width: 0;
  ${(props) => props.theme.breakpoints.down("sm")} {
    min-width: 68px;
  }
`;

type PropertyTab = Extract<WidgetTab, { kind: 'property' }>
type GroupTab = Extract<WidgetTab, { kind: 'group' }>

const MenuTabs: FC<{
  currentStandard: A11yStandardType;
  properties: Property[];
  tabs: WidgetTab[];
  buildingArea: BuildArea | undefined;
}> = ({
  buildingArea,
  properties,
  tabs,
  currentStandard
}) => {
  const [field, setField] = useContext(TabsContext);
  const { t } = useTranslation();
  const groupTabs = tabs.filter((tab): tab is GroupTab => tab.kind === 'group');
  const propertyTabs = tabs.filter((tab): tab is PropertyTab => tab.kind === 'property');
  const hasMalls = properties && properties.some((i) => i.category === propertyCategoryKeys.mallShoppingComplex);

  const onTabChange = (event: React.SyntheticEvent, newValue: TabKey) => {
    setField(newValue);
    if (buildingArea && newValue === MAIN_TAB) {
      Mixpanel.track(`${MetricMessage.accessed_building_area}`);
    } else if (buildingArea) {
      Mixpanel.track(`${MetricMessage.accessed_property_tab}`);
    }
  };

  const getTabs = () => {
    // property tabs are hidden, only the open one is shown, with a button closing it
    if (currentStandard?.key === a11yStandardKeys.outdoorPlayground || groupTabs.length > 0 || !hasMalls) {
      return propertyTabs.map((tab) => (
        <Tab
          style={{display: tab.key === field ? 'inline-flex' : 'none'}}
          key={tab.key}
          value={tab.key}
          label={
            <BlockLabel>
              {getTextByCurrentLanguage(tab.property.name)}
              <EmulatedButton
                  onClick={() => setField(MAIN_TAB)}
              >
                <StyledCloseIcons />
              </EmulatedButton>
            </BlockLabel>
          }
        />
      ))
    }

    // a building area with shopping malls shows a tab for every mall
    return propertyTabs
      .filter((tab) => tab.property.category === propertyCategoryKeys.mallShoppingComplex)
      .map((tab) => (
        <Tab
          key={tab.key}
          value={tab.key}
          label={getTextByCurrentLanguage(tab.property.name)}
        />
      ))
  }

  const getLabel = () => {
    if (currentStandard && currentStandard.key === a11yStandardKeys.outdoorPlayground) {
      return t('playground_information')
    }
    return buildingArea?.name
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      {buildingArea && (
        <Tabs
          variant="scrollable"
          value={field}
          onChange={onTabChange}
          aria-label="tabs"
          allowScrollButtonsMobile
        >
          <Tab label={getLabel()} value={MAIN_TAB} />
          {groupTabs.map((tab) => (
            <Tab label={t(tab.group.labelKey)} key={tab.key} value={tab.key} />
          ))}
          {getTabs()}
        </Tabs>
      )}
    </Box>
  )
}


export const PageLayout: FC<{
  fullScreen: boolean;
  loading: boolean;
  currentStandard: A11yStandardType;
  companyLogo: string;
  propertyName: string;
  properties: Property[];
  tabs: WidgetTab[];
  headerTexts: Record<TabKey, string>;
  buildingArea: BuildArea | undefined;
  companyName: string;
  supportedLanguages: string[];
}> = ({
  fullScreen,
  loading,
  headerTexts,
  children,
  currentStandard,
  companyLogo,
  propertyName,
  properties,
  tabs,
  buildingArea,
  companyName,
  supportedLanguages,
}) => {

  const { height } = useElementHeight({
    elemId: "content-wrapper",
  }, [children]);

  const getHeight = (height: number, loading: boolean, fullscreen: boolean): string => {
    let result: string = '100%'
    if (fullscreen && loading) {
      result = '100vh'
    }
    if (!fullscreen && loading) {
      result = `${height}px`
    }
    return result
  }

  const getHeightData = useMemo(() => {
    return getHeight(height, loading, fullScreen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, height])

  return (
    <Root id="root-content">
      <AppContent>
        <Header
          headerTexts={headerTexts}
          companyLogo={companyLogo}
          propertyName={propertyName}
          companyName={companyName}
          supportedLanguages={supportedLanguages}
        />
      </AppContent>
      <ContentWrapper id="content-wrapper">
        <MainContent
          square
          height={getHeightData}
        >
          {(properties.length > 1) ?
            <MenuTabs
              buildingArea={buildingArea}
              properties={properties}
              tabs={tabs}
              currentStandard={currentStandard}
            /> : null
          }
          {children}
        </MainContent>
      </ContentWrapper>
    </Root>
  );
};
